import { ref } from "vue";
import useBackendClient from "@/client";
import useToast from "@/hooks/useToastNotification";
import { AbTestMetadata, UserAbTestAssignment, UserAssignmentRow } from "@/modules/UserInspector/types";

const assignments = ref<UserAssignmentRow[]>([]);
const isLoading = ref(false);
const hasSearched = ref(false);

export default function useUserInspector() {
  const { client } = useBackendClient();
  const { toastError } = useToast();

  /**
   * Fetch the (already-filtered) live AB test assignments for a user, then enrich
   * them with AB test / cohort names for display. Name resolution is best-effort:
   * if the metadata fetch fails we still render the assignments with their raw ids.
   */
  async function inspect(gameId: string, versionId: string, userUUID: string) {
    const uuid = userUUID.trim();
    if (!uuid) {
      return;
    }

    isLoading.value = true;
    hasSearched.value = true;

    try {
      const { data: rawAssignments } = await client.get<UserAbTestAssignment[]>(
        `/app/${gameId}/users/${uuid}/ab-test-assignments`
      );

      const abTestNameById = new Map<string, string>();
      const cohortNameById = new Map<string, string>();
      const segmentLabelByAbTestId = new Map<string, string>();

      try {
        const { data: abTests } = await client.get<AbTestMetadata[]>(`/app/${gameId}/version/${versionId}/ab-tests/`);
        abTests.forEach((abTest) => {
          abTestNameById.set(abTest.id, abTest.name);
          (abTest.cohorts ?? []).forEach((cohort) => cohortNameById.set(cohort.id, cohort.name));
          const segmentLabel = (abTest.segments ?? [])
            .map((segmentId) => abTest.segmentNames?.[segmentId] ?? segmentId)
            .join(", ");
          segmentLabelByAbTestId.set(abTest.id, segmentLabel);
        });
      } catch {
        // Names are a nice-to-have; fall back to ids if the metadata fetch fails.
      }

      assignments.value = rawAssignments.map((assignment) => ({
        abTestId: assignment.abTestId,
        abTestName: abTestNameById.get(assignment.abTestId) ?? assignment.abTestId,
        clusterId: assignment.clusterId,
        cohortName: (assignment.clusterId && cohortNameById.get(assignment.clusterId)) || assignment.clusterId,
        segments: segmentLabelByAbTestId.get(assignment.abTestId) ?? "",
        kind: assignment.type === "manual" ? "Manual" : "System",
        createdAt: assignment.createdAt,
      }));
    } catch (error) {
      assignments.value = [];
      toastError({
        detail: "An error occurred while fetching the user's AB test assignments.",
        summary: "Error",
      });
    } finally {
      isLoading.value = false;
    }
  }

  function reset() {
    assignments.value = [];
    hasSearched.value = false;
  }

  return {
    assignments,
    isLoading,
    hasSearched,
    inspect,
    reset,
  };
}
